import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Banner } from '@/components/ui/Feedback';
import { useAuth } from '@/features/auth/AuthContext';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { instrumentApi, type InstrumentDetail } from './api';

const STATUS_LABEL = {
  draft: 'Draf',
  published: 'Terbit',
  archived: 'Arsip',
} as const;

/**
 * Bilah versi: berpindah versi, membuat draf, menerbitkan, membuang draf.
 *
 * Ditaruh di atas tab karena versi menentukan ARTI seluruh isi di bawahnya —
 * membaca angka bobot tanpa tahu sedang melihat draf atau arsip mudah
 * menyesatkan.
 */
export function VersionBar({
  data,
  viewing,
  onView,
}: {
  data: InstrumentDetail;
  viewing: number | null;
  onView: (version: number | null) => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = user?.role === 'super_admin';

  const [confirm, setConfirm] = useState<'publish' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const current = data.version;
  const draft = data.versions.find((v) => v.status === 'draft');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['instrument'] });
  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Gagal memproses versi.');

  const create = useMutation({
    mutationFn: () => instrumentApi.createVersion(data.id),
    onSuccess: (res) => {
      refresh();
      setError(null);
      onView(res.version);
      setBanner(`Draf v${res.version} dibuat — salinan lengkap dari versi terbit.`);
    },
    onError: fail,
  });

  const publish = useMutation({
    mutationFn: () => instrumentApi.publishVersion(data.id, draft!.id),
    onSuccess: (res) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ['instruments'] });
      setConfirm(null);
      setError(null);
      onView(null);
      setBanner(`v${res.version} diterbitkan. Versi sebelumnya kini menjadi arsip.`);
    },
    onError: (err) => {
      setConfirm(null);
      fail(err);
    },
  });

  const discard = useMutation({
    mutationFn: () => instrumentApi.deleteVersion(data.id, draft!.id),
    onSuccess: () => {
      refresh();
      setConfirm(null);
      setError(null);
      onView(null);
      setBanner('Draf dibuang.');
    },
    onError: (err) => {
      setConfirm(null);
      fail(err);
    },
  });

  return (
    <>
      {banner && <Banner tone="success">{banner}</Banner>}
      {error && <Banner tone="danger">{error}</Banner>}

      {current.status === 'archived' && (
        <Banner tone="info">
          Anda sedang membaca <strong>arsip v{current.version}</strong> — rekaman instrumen saat
          penilaian lama diberikan. Isinya tidak bisa diubah.
        </Banner>
      )}

      <Card className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium text-ink-secondary">Versi:</span>
            {data.versions.map((v) => (
              <button
                key={v.id}
                onClick={() => onView(v.id === current.id ? viewing : v.version)}
                className={cn(
                  'tabular min-h-9 rounded-full border px-3 text-sm font-medium',
                  v.id === current.id
                    ? 'border-primary bg-primary text-white'
                    : 'border-border bg-surface text-ink hover:border-primary',
                )}
              >
                v{v.version} · {STATUS_LABEL[v.status]}
              </button>
            ))}
          </div>

          {canEdit && (
            <div className="flex flex-wrap gap-2">
              {draft ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setConfirm('discard')}>
                    Buang Draf
                  </Button>
                  <Button size="sm" onClick={() => setConfirm('publish')}>
                    Terbitkan v{draft.version}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => create.mutate()}
                  loading={create.isPending}
                >
                  + Versi Baru
                </Button>
              )}
            </div>
          )}
        </div>

        {draft && current.status === 'draft' && (
          <p className="mt-3 rounded-[var(--radius-input)] bg-warn-soft px-3.5 py-2.5 text-[13px] text-[#9a6400]">
            Anda menyunting <strong>draf v{draft.version}</strong>. Versi terbit tetap dipakai
            penilaian sampai draf ini diterbitkan.
          </p>
        )}
      </Card>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm === 'publish' ? `Terbitkan v${draft?.version}?` : 'Buang draf?'}
        confirmLabel={confirm === 'publish' ? 'Ya, terbitkan' : 'Ya, buang'}
        tone={confirm === 'publish' ? 'primary' : 'danger'}
        loading={publish.isPending || discard.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => (confirm === 'publish' ? publish.mutate() : discard.mutate())}
      >
        {confirm === 'publish' ? (
          <p>
            Mulai saat itu, penilaian baru memakai v{draft?.version}. Versi terbit sekarang menjadi
            <strong> arsip</strong> — tidak dihapus, karena penilaian lama masih merujuk padanya.
            Σ bobot tiap bagian harus tepat 100; kalau belum, penerbitan ditolak.
          </p>
        ) : (
          <p>
            Seluruh perubahan pada draf v{draft?.version} hilang dan tidak bisa dikembalikan. Versi
            terbit tidak terpengaruh.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
